/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */

import React, { useState } from "react";
import { Box, Chip, Divider, Grid, Icon } from "@mui/material";
import BasicModal from "examples/modal/BasicModal/BasicModal";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import pxToRem from "assets/theme/functions/pxToRem";
import moment from "moment";
import FullScreenImageComponent from "components/ViewFullImage/ViewImage";
import { policyStatusStyles } from "dataTables/insurance/insuranceTable";
import { Icons } from "utils/Constants";
import Pdf from "assets/images/pdf.svg";

const formatDate = (dateVal) => {
  if (!dateVal) return "-";
  return moment(dateVal).format("DD-MM-YYYY");
};

const formatCurrency = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  const num = Number(val);
  if (Number.isNaN(num)) return "-";
  return `₹ ${num.toLocaleString("en-IN")}`;
};

function InfoField({ label, value, isRightAligned = false }) {
  return (
    <MDBox display="flex" flexDirection="column" gap={0.5}>
      <MDTypography variant="caption" sx={{ color: "#667085", fontWeight: 500, fontSize: 13 }}>
        {label}
      </MDTypography>
      <MDTypography
        variant="body2"
        sx={{
          color: "#344054",
          fontWeight: 600,
          fontSize: 14,
          textAlign: isRightAligned ? "right" : "left",
        }}
      >
        {value || "-"}
      </MDTypography>
    </MDBox>
  );
}

export default function ViewInsuranceModal({ open, handleClose, insurance }) {
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [fullScreenImageIndex, setFullScreenImageIndex] = useState(0);

  if (!insurance) return null;

  const statusStyle = policyStatusStyles[insurance.policyStatus] || policyStatusStyles.default;
  const attachments = Array.isArray(insurance.attachments) ? insurance.attachments : [];

  const handleImageFullView = (url, index) => {
    const isPdf =
      typeof url === "string" &&
      (url.toLowerCase().includes(".pdf") ||
        attachments[index]?.name?.toLowerCase().includes(".pdf"));
    if (isPdf) {
      window.open(url, "_blank");
      return;
    }
    setFullScreenImageIndex(index);
    setFullScreenImage(url);
  };

  const handleNextImage = () => {
    if (attachments.length === 0) return;
    const nextIdx = (fullScreenImageIndex + 1) % attachments.length;
    setFullScreenImageIndex(nextIdx);
    setFullScreenImage(attachments[nextIdx]?.url);
  };

  const handlePreviousImage = () => {
    if (attachments.length === 0) return;
    const prevIdx = (fullScreenImageIndex - 1 + attachments.length) % attachments.length;
    setFullScreenImageIndex(prevIdx);
    setFullScreenImage(attachments[prevIdx]?.url);
  };

  return (
    <>
      <BasicModal
        open={open}
        handleClose={handleClose}
        title="View Policy Details"
        actionButton="Close"
        handleAction={handleClose}
        minWidth={780}
      >
        <Grid container spacing={2.5}>
          {/* Header Summary Banner */}
          <Grid item xs={12}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1.5}
              sx={{
                p: 2,
                borderRadius: "8px",
                backgroundColor: "#F9FAFB",
                border: "1px solid #EAECF0",
              }}
            >
              <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <MDTypography
                  variant="h6"
                  sx={{ fontSize: pxToRem(17), fontWeight: 700, color: "#101828" }}
                >
                  {insurance.customerName}
                </MDTypography>
                {insurance.insuranceId && (
                  <Chip
                    label={`#${insurance.insuranceId}`}
                    size="small"
                    sx={{ fontWeight: 600, backgroundColor: "#E0EAFF", color: "#2970FF" }}
                  />
                )}
                <Chip
                  label={insurance.product || "Insurance"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#F2F4F7",
                    color: "#344054",
                  }}
                />
                <Chip
                  label={insurance.policyStatus || "Active"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: statusStyle.backgroundColor,
                    color: statusStyle.color,
                  }}
                />
              </MDBox>
              <MDBox display="flex" gap={3} alignItems="center">
                {insurance.grossPremium ? (
                  <MDBox textAlign="right">
                    <MDTypography variant="caption" sx={{ color: "#667085", fontWeight: 500 }}>
                      Gross Premium
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: "#027A48", fontWeight: 700 }}>
                      {formatCurrency(insurance.grossPremium)}
                    </MDTypography>
                  </MDBox>
                ) : null}
                {insurance.netPremium ? (
                  <MDBox textAlign="right">
                    <MDTypography variant="caption" sx={{ color: "#667085", fontWeight: 500 }}>
                      Net Premium
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: "#175CD3", fontWeight: 700 }}>
                      {formatCurrency(insurance.netPremium)}
                    </MDTypography>
                  </MDBox>
                ) : null}
              </MDBox>
            </MDBox>
          </Grid>

          {/* Customer Information */}
          <Grid item xs={12}>
            <MDTypography
              variant="subtitle2"
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#344054",
                borderBottom: "1px solid #EAECF0",
                pb: 0.5,
                mb: 1.5,
              }}
            >
              Customer Details
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <InfoField label="Customer Name" value={insurance.customerName} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="Mobile Number" value={insurance.mobileNumber} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="Email ID" value={insurance.email} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="Reference" value={insurance.reference} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="IMD" value={insurance.imd} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="Month" value={insurance.month} />
              </Grid>
            </Grid>
          </Grid>

          {/* Policy & Provider Details */}
          <Grid item xs={12}>
            <MDTypography
              variant="subtitle2"
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#344054",
                borderBottom: "1px solid #EAECF0",
                pb: 0.5,
                mb: 1.5,
              }}
            >
              Policy & Provider Details
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <InfoField label="Product" value={insurance.product} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="Insurance Company" value={insurance.insuranceCompany} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="Name of Plan" value={insurance.planName} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="Policy Number" value={insurance.policyNumber} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="Code / Portal" value={insurance.code} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="Current Status" value={insurance.policyStatus} />
              </Grid>
            </Grid>
          </Grid>

          {/* Dates & Financials */}
          <Grid item xs={12}>
            <MDTypography
              variant="subtitle2"
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#344054",
                borderBottom: "1px solid #EAECF0",
                pb: 0.5,
                mb: 1.5,
              }}
            >
              Dates & Financials
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <InfoField label="Inquiry Date" value={formatDate(insurance.inquiryDate)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField
                  label="Policy Issue Date"
                  value={formatDate(insurance.policyIssueDate)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoField label="Renewal Date" value={formatDate(insurance.renewalDate)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField label="Gross Premium" value={formatCurrency(insurance.grossPremium)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField label="Net Premium" value={formatCurrency(insurance.netPremium)} />
              </Grid>
            </Grid>
          </Grid>

          {/* Attachments Section - Matches Lead Bay 70x70 Thumbnails */}
          <Grid item xs={12}>
            <MDTypography
              variant="subtitle2"
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#344054",
                borderBottom: "1px solid #EAECF0",
                pb: 0.5,
                mb: 1.5,
              }}
            >
              Attached Documents ({attachments.length})
            </MDTypography>
            {attachments.length === 0 ? (
              <MDTypography variant="caption" sx={{ color: "#667085", fontStyle: "italic" }}>
                No attachments uploaded for this policy.
              </MDTypography>
            ) : (
              <MDBox
                display="flex"
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="center"
                gap={1.5}
                flexWrap="wrap"
              >
                {attachments.map((img, i) => (
                  <MDBox
                    display="flex"
                    borderRadius="8px"
                    position="relative"
                    key={img.url ? img.url : `view-attachment-${img.name}`}
                    sx={{
                      "&:hover .overlay": { display: "flex", borderRadius: "8px" },
                    }}
                  >
                    <img
                      src={
                        img?.name?.toLowerCase().includes(".pdf") ||
                        img?.url?.toLowerCase().includes(".pdf")
                          ? Pdf
                          : img?.url
                      }
                      alt="Preview"
                      width={70}
                      height={70}
                      style={{ borderRadius: "8px", objectFit: "cover" }}
                    />
                    <Box
                      display="none"
                      position="absolute"
                      top={0}
                      right={0}
                      bottom={0}
                      left={0}
                      alignItems="center"
                      justifyContent="center"
                      bgcolor="rgba(0, 0, 0, 0.5)"
                      className="overlay"
                    >
                      <Icon
                        sx={{
                          color: "white",
                          width: 28,
                          height: 28,
                          cursor: "pointer",
                        }}
                        onClick={() => handleImageFullView(img?.url, i)}
                      >
                        {Icons.VIEW2}
                      </Icon>
                    </Box>
                  </MDBox>
                ))}
              </MDBox>
            )}
          </Grid>

          {/* Audit Trail */}
          {insurance.createdAt && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <MDBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <MDTypography variant="caption" sx={{ color: "#98A2B3" }}>
                  Created on: {moment(insurance.createdAt).format("DD-MM-YYYY hh:mm A")}
                  {insurance.creatorName ? ` by ${insurance.creatorName}` : ""}
                </MDTypography>
                {insurance.updatedAt && (
                  <MDTypography variant="caption" sx={{ color: "#98A2B3" }}>
                    Last updated: {moment(insurance.updatedAt).format("DD-MM-YYYY hh:mm A")}
                  </MDTypography>
                )}
              </MDBox>
            </Grid>
          )}
        </Grid>
      </BasicModal>

      {/* Full screen image preview modal matching Lead Bay */}
      {fullScreenImage && (
        <FullScreenImageComponent
          fullScreenImage={fullScreenImage}
          handleCloseFullView={() => setFullScreenImage(null)}
          handleNextImage={handleNextImage}
          handlePreviousImage={handlePreviousImage}
          image={attachments}
          src={fullScreenImage}
        />
      )}
    </>
  );
}
